import { useEffect, useState } from 'react';
import { ContentLoader } from '@rio-cloud/rio-uikit';
import Notification from '@rio-cloud/rio-uikit/Notification';

import { exportAegOccurrencePdf } from '../../../../api/aegOccurrences';
import { useAegDetails } from '../../../../hooks/useAegDetails';
import { useAegOccurrenceChat } from '../../../../hooks/useAegOccurrenceChat';
import { useAegOccurrenceFiles } from '../../../../hooks/useAegOccurrenceFiles';
import { useAegSupportHistory } from '../../../../hooks/useAegSupportHistory';

import Attachments from '../Attachments';
import Chat from '../Chat';
import Summary from '../Detail';
import Select from '../Select';
import SupportHistory from '../SupportHistory';
import VehicleHistory from '../VehicleHistory';
import { StagePill } from '../Table';
import { useIntl } from 'react-intl';

import { allStages } from '../../config';
import {
    AEG_NEXT_STAGES,
    AEG_STATUS_BY_STAGE,
    formatAegStage,
    getAegAssignToOptions,
    type StageKey,
} from '../../types';
import './styles.css';

type RequestDetailProps = {
    id: string;
    onClose: () => void;
    onMove: (stage: StageKey) => void;
    onAssign: (assignTo: string) => void;
    onSaved: () => void;
};

const visible = (value?: string | null) => (value && value !== '—' ? value : '');

export default function RequestDetail({ id, onClose, onMove, onAssign, onSaved }: RequestDetailProps) {
    const intl = useIntl();
    const label = (messageId: string) => intl.formatMessage({ id: `intl-msg:${messageId}` });
    const [tab, setTab] = useState('resumo');
    const { details, loading, error, save } = useAegDetails(id);
    const [saving, setSaving] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const chat = useAegOccurrenceChat(id);
    const attachments = useAegOccurrenceFiles(id);
    const history = useAegSupportHistory(id);
    const [stageOverride, setStageOverride] = useState<StageKey | null>(null);
    const [assignOverride, setAssignOverride] = useState<string | null>(null);
    const savedStage = details?.stage;
    const stage = stageOverride ?? savedStage;
    const nextStages = savedStage ? AEG_NEXT_STAGES[savedStage] : [];
    const assignTo = assignOverride ?? details?.assignTo ?? '';
    const client = visible(details?.client);
    const model = visible(details?.model);
    const plate = visible(details?.plate);
    const tabs = [
        { key: 'resumo', label: label('support.aeg.detail.tab.summary') },
        { key: 'chat', label: label('support.aeg.detail.tab.chat'), count: chat.comments.length },
        { key: 'historico', label: label('support.aeg.detail.tab.history'), count: history.total },
        { key: 'anexos', label: label('support.aeg.newRequest.section.attachments'), count: attachments.files.length },
        { key: 'veiculo', label: label('support.aeg.detail.tab.vehicle') },
    ];

    useEffect(() => {
        setStageOverride(null);
    }, [id, details?.stage]);

    useEffect(() => {
        setAssignOverride(null);
    }, [id, details?.assignTo]);

    const handleMove = (value: string) => {
        const nextStage = value as StageKey;
        if (!savedStage || (nextStage !== savedStage && !nextStages.includes(nextStage))) {
            return;
        }
        setStageOverride(nextStage);
        onMove(nextStage);
    };

    const handleAssign = (value: string) => {
        setAssignOverride(value);
        onAssign(value);
    };

    const handleExportPdf = async () => {
        if (exportingPdf || !id) {
            return;
        }

        setExportingPdf(true);
        try {
            const blob = await exportAegOccurrencePdf(id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `aeg-${details?.aegNumber ?? id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            Notification.error(label('support.aeg.detail.exportPdf.error'));
        } finally {
            setExportingPdf(false);
        }
    };

    const handleSave = async () => {
        if (saving || loading || !details) {
            return;
        }

        setSaving(true);
        try {
            await save({
                status: stage ? AEG_STATUS_BY_STAGE[stage] : undefined,
                assignTo,
            });
            Notification.success(label('support.aeg.detail.save.success'));
            onSaved();
            onClose();
        } catch {
            Notification.error(label('support.aeg.detail.save.error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className='aeg-request-detail-scrim bg-black opacity-50' onClick={onClose} />
            <dialog
                aria-busy={loading}
                aria-modal='true'
                className='aeg-request-detail-modal display-flex flex-column bg-white text-size-14'
                open
            >
                <header className='display-flex align-items-center gap-10 padding-15 border border-bottom-only border-left-none border-right-none border-top-none'>
                    <div className='flex-1-1'>
                        <div className='display-flex align-items-center flex-wrap gap-10'>
                            <h2 className='aeg-request-detail-title margin-0 text-bold text-color-darkest'>
                                {label('support.aeg.detail.title')}
                            </h2>
                            {loading ? (
                                <ContentLoader height={22} width={96} />
                            ) : stage ? (
                                <StagePill stage={stage} />
                            ) : null}
                        </div>
                        <span className='display-block margin-top-5 text-size-12 text-color-dark'>
                            {loading ? (
                                <ContentLoader height={12} width={140} />
                            ) : details?.aegNumber ? (
                                <span className='aeg-request-detail-mono'>{details.aegNumber}</span>
                            ) : (
                                <i>{label('support.aeg.detail.summary.aegNumberPending')}</i>
                            )}
                            {loading ? (
                                <>
                                    {' · '}
                                    <ContentLoader height={12} width={220} />
                                </>
                            ) : (
                                <>
                                    {client ? ` · ${client}` : ''}
                                    {model ? ` · ${model}` : ''}
                                    {plate ? (
                                        <>
                                            {' · '}
                                            <span className='aeg-request-detail-mono'>{plate}</span>
                                        </>
                                    ) : null}
                                </>
                            )}
                        </span>
                    </div>
                    <button
                        aria-label={label('support.aeg.newRequest.close')}
                        className='btn btn-link padding-0'
                        onClick={onClose}
                        type='button'
                    >
                        <span aria-hidden='true' className='aeg-request-detail-close-icon rioglyph rioglyph-remove' />
                    </button>
                </header>
                <div className='aeg-request-detail-body flex-1-1 overflow-auto'>
                    <section className='aeg-request-detail-movebar display-flex align-items-end flex-wrap gap-10 padding-15 bg-lightest border rounded'>
                        <label className='aeg-request-detail-movebar-field display-flex flex-column gap-5 margin-0'>
                            <span className='text-size-12 text-medium text-color-darker'>
                                {label('support.aeg.newRequest.field.moveTo')}
                            </span>
                            <Select
                                ariaLabel={label('support.aeg.newRequest.field.moveTo')}
                                disabled={!savedStage || nextStages.length === 0}
                                fullWidth
                                loading={loading}
                                onChange={handleMove}
                                options={allStages.map(item => ({
                                    value: item,
                                    label: formatAegStage(intl, item),
                                    disabled: item !== savedStage && !nextStages.includes(item),
                                }))}
                                value={stage ?? ''}
                            />
                        </label>
                        <label className='aeg-request-detail-movebar-field display-flex flex-column gap-5 margin-0'>
                            <span className='text-size-12 text-medium text-color-darker'>
                                {label('support.aeg.detail.summary.assignedTo')}
                            </span>
                            <Select
                                ariaLabel={label('support.aeg.detail.summary.assignedTo')}
                                fullWidth
                                loading={loading}
                                onChange={handleAssign}
                                options={getAegAssignToOptions(intl)}
                                value={assignTo}
                            />
                        </label>
                        <button
                            className={`btn btn-default height-40 margin-left-auto ${exportingPdf ? 'btn-loading' : ''}`}
                            disabled={exportingPdf || !id}
                            onClick={() => {
                                void handleExportPdf();
                            }}
                            type='button'
                        >
                            {label('support.aeg.detail.exportPdf')}
                        </button>
                    </section>
                    <nav className='aeg-request-detail-tabs'>
                        {tabs.map(item => (
                            <button
                                aria-selected={tab === item.key}
                                className={`bg-transparent text-medium ${
                                    tab === item.key ? 'active text-bold text-color-darkest' : 'text-color-darker'
                                }`}
                                key={item.key}
                                onClick={() => setTab(item.key)}
                                type='button'
                            >
                                {item.label}
                                {item.count ? (
                                    <small className='margin-left-5 padding-x-5 rounded bg-lighter text-color-dark'>
                                        {item.count}
                                    </small>
                                ) : null}
                            </button>
                        ))}
                    </nav>
                    {tab === 'resumo' ? (
                        <Summary assignTo={assignTo} details={details} error={error} loading={loading} stage={stage} />
                    ) : tab === 'chat' ? (
                        <Chat {...chat} />
                    ) : tab === 'historico' ? (
                        <SupportHistory {...history} />
                    ) : tab === 'anexos' ? (
                        <Attachments {...attachments} />
                    ) : (
                        <VehicleHistory chassis={details?.chassis || ''} />
                    )}
                </div>
                <footer className='display-flex justify-content-end gap-10 padding-15 border border-top-only border-left-none border-right-none border-bottom-none'>
                    <button className='btn btn-default' disabled={saving} onClick={onClose} type='button'>
                        {label('support.aeg.newRequest.button.cancel')}
                    </button>
                    <button
                        className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
                        disabled={saving || loading || !details}
                        onClick={() => {
                            void handleSave();
                        }}
                        type='button'
                    >
                        {label('support.aeg.detail.button.save')}
                    </button>
                </footer>
            </dialog>
        </>
    );
}
